/**
 * useSplitColumnWarnings
 *
 * Pre-flight for the per-target send (SYD-2302): confirms that every table the
 * send renders carries the column the workflow splits by. Its only consumer is
 * `WorkflowParameterContent`, which folds `warnings` into `configWarnings` so the
 * banner names the table and Run stays disabled.
 *
 * The predicate is `resolveSplitForTable`'s own — a column whose `typeId` equals
 * `splitRef.valuePath` — evaluated ahead of the build over the table type's
 * `columns`, the same set `ArTableBody` hands `useArTableSplit`. At send time that
 * resolver returns `null` for a table without the column and `null` means no
 * filtering, so every recipient's document would carry every recipient's rows.
 *
 * Fails closed: a rendered table without the column is a warning whether or not it
 * holds rows. The operator has three levers and the message names all three —
 * pick an export view the table is hidden in, hide it in the one they picked, or
 * add the column to the table.
 *
 * `tableIds` decides the scope, and the caller owns it: nothing here knows which
 * export view rendered them. `warnings` is empty while `loading`, and both callers
 * — Run and Preview PDF — gate on `loading` too, so neither can act on a scope the
 * pre-flight has not verified. `loading` is false whenever `tableIds` is empty, so
 * a sidebar with no fan-out never paints the banner.
 */

import { useMemo } from 'react';
import type { ArTableType } from '#types/System.Artifacts.types';
import { useStoreDocsByPaths } from '@/store';

type UseSplitColumnWarningsParams = {
  /** The fan-out declaration. `undefined` disables the check and subscribes to nothing. */
  split: { columnId: string } | undefined;
  /** Tables the send renders, across every export view its documents are built at. */
  tableIds: readonly string[];
  /** The form type the tables belong to, named in the message. `undefined` before it loads. */
  formTypeLabel: string | undefined;
};

type UseSplitColumnWarningsResult = {
  loading: boolean;
  warnings: string[];
};

export function useSplitColumnWarnings({
  split,
  tableIds,
  formTypeLabel,
}: UseSplitColumnWarningsParams): UseSplitColumnWarningsResult {
  const columnId = split?.columnId;
  const paths = useMemo(
    () => (columnId === undefined ? [] : tableIds.map((tableId) => `tableTypes/${tableId}`)),
    [columnId, tableIds],
  );
  const { data, loading: pathsLoading } = useStoreDocsByPaths<ArTableType>(paths);

  /**
   * `useStoreDocsByPaths` seeds its aggregate `loading` at `true` and clears it in
   * an effect, so an empty path list reads as loading for one committed frame —
   * long enough for every sidebar to paint the banner and disable Run.
   */
  const loading = paths.length > 0 && pathsLoading;

  const warnings = useMemo(() => {
    if (columnId === undefined || loading) return [];
    return paths.flatMap((path, index) => {
      const schema = data.get(path);
      if (schema?.columns.some((column) => column.typeId === columnId)) return [];
      const tableLabel = schema?.description?.shortLabel ?? tableIds[index];
      return [
        `The ${tableLabel} table in ${formTypeLabel ?? 'this form type'} has no "${columnId}" column, so this send cannot split it per recipient. Choose an Export view that hides the table, hide it in the view you picked, or add the column to the table.`,
      ];
    });
  }, [columnId, loading, paths, data, tableIds, formTypeLabel]);

  return { loading, warnings };
}
